
import type { ApolloClient, NormalizedCacheObject } from "@apollo/client/core";
import { createDataClient } from "~/packages/data-client/client";

import { ContextManager, ContextInstance } from "~/modules/contextManager/ContextManager";
import { DataClient } from "~/modules/dataClient/DataClient";
import { Logger, getLogger, LoggerName } from "~/modules/logger";
import { Session } from "~/modules/session";
import { SessionImpl } from "~/modules/session/Session/SessionImpl";
import { ClientOptionsStore } from "~/modules/client/ClientOptions/ClientOptionsStore";
import { getConfigRegion } from "~/utils/regionUtil";

export class DataClientImpl implements DataClient {
    readonly #logger: Logger;

    readonly #session: Session;

    readonly #clientOptions: ClientOptionsStore;

    client: ApolloClient<NormalizedCacheObject> | null;

    constructor(ctx: ContextManager) {
        this.#session = ctx.retrieveInstanceOf(ContextInstance.SessionImpl, SessionImpl);
        this.#logger = getLogger(ctx)(LoggerName.DataClient);
        this.#clientOptions = ctx.retrieveInstanceOf(ContextInstance.ClientOptionsStore, ClientOptionsStore);
        this.client = null;
    }

    async init(): Promise<ApolloClient<NormalizedCacheObject> | null> {
        this.#logger.debug("Initializing dataClient");

        const getTokenFromSession = () => {
            return this.#session.token;
        };

        if (this.client) {
            return this.client;
        }

        const { dataClientOptions } = this.#clientOptions;
        const configRegion = getConfigRegion(this.#clientOptions);

        this.client = await createDataClient(getTokenFromSession, { ...dataClientOptions, region: configRegion })
            .then((client) => {
                this.#logger.debug("Initialized dataClient successfully");
                return client;
            })
            .catch((err) => {
                this.#logger.error("Failed to initialize dataClient", err);
                return null;
            });
        return this.client;
    }

    destroy(): void {
        if (this.client) {
            
            this.client.stop();
        }

        this.client = null;
    }
}
